import getAuthzCheck, {
  ResourceCheck,
} from "@fartherfinance/frontend/api/Authz/requests/getAuthzCheck";

import { ExpressionResolverFn } from "../../core/types";
import { ExpressionResolverError } from "../errors";

class HasPermissionExpressionError extends ExpressionResolverError {
  constructor(message: string) {
    super(message);
    this.name = "HasPermissionExpressionError";
  }
}

/**
 * The has-permission resolver checks if the current user has permission to perform
 * a specific action on a resource using Farther's authorization middleware.
 *
 * Usage:
 * {
 *   function: "has-permission",
 *   params: {
 *     resourceType: {
 *       value: "Task"  // Organization | TradingGroup | FartherAccount | PortfolioPlan | Task | FinOps
 *     },
 *     resourceId: {
 *       value: "task-123"  // or null for resource-level checks
 *       // OR
 *       ref: "taskId"  // Reference to a field value
 *     },
 *     action: {
 *       value: "edit"  // Action varies by resource type
 *     }
 *   }
 * }
 */
export const hasPermissionResolver: ExpressionResolverFn = async (config) => {
  const { expression, externalResources, templateFieldValueResolver } = config;
  const { params } = expression;

  if (!params) {
    throw HasPermissionExpressionError.create("No parameters provided");
  }

  // Extract authConfig from external resources
  if (!externalResources?.authConfig) {
    throw HasPermissionExpressionError.create(
      "Auth configuration is not available in external resources. Ensure authConfig is provided."
    );
  }

  // Extract resourceType parameter
  if (!params.resourceType || !("value" in params.resourceType)) {
    throw HasPermissionExpressionError.create(
      "Missing resourceType.value parameter"
    );
  }

  const resourceType = params.resourceType.value as string;

  // Extract resourceId parameter
  let resourceId: string | null = null;
  if (params.resourceId) {
    if ("value" in params.resourceId) {
      resourceId = params.resourceId.value as string | null;
    } else if ("ref" in params.resourceId) {
      const fieldValue = templateFieldValueResolver(
        typeof params.resourceId.ref === "string"
          ? params.resourceId.ref
          : String(params.resourceId.ref)
      );
      resourceId = fieldValue as string | null;
    }
  }

  // Extract action parameter
  if (!params.action || !("value" in params.action)) {
    throw HasPermissionExpressionError.create("Missing action.value parameter");
  }

  const action = params.action.value as string;

  // Construct the permission check request
  const permissionCheck: ResourceCheck = {
    type: resourceType,
    id: resourceId,
    action: action,
  } as ResourceCheck;

  try {
    // Call the authorization middleware
    const result = await getAuthzCheck(
      permissionCheck,
      externalResources.authConfig
    );
    return result.authorized;
  } catch (error) {
    // Log the error but fail safely by returning false
    console.warn("Permission check failed:", error);
    return false;
  }
};
